import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Loader2, CheckCircle, XCircle, Music, Headphones } from 'lucide-react';
import { toast } from 'sonner';

const APP_ICON_URL = "/sidebar-logo.png";

export const AccessGatePage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [grantedRole, setGrantedRole] = useState(null);
  const { axiosInstance, refreshUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error('Please enter an access code');
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.post('/access/validate', { code: code.trim() });
      if (res.data.success) {
        setSuccess(true);
        setGrantedRole(res.data.role || 'listener');
        const roleMsg = res.data.role === 'artist' ? "Welcome, Artist!" : "You're in!";
        toast.success(roleMsg);
        // Refresh user data to get updated has_access and role
        await refreshUser();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const LOGO_URL = "/wordmark-tight.png";

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6">
      {/* Logo */}
      <img 
        src={LOGO_URL} 
        alt="AUDIVUE" 
        className="h-24 sm:h-32 object-contain mb-8"
      />

      {/* Gate Card */}
      <div className="w-full max-w-sm">
        {success ? (
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
              grantedRole === 'artist' ? 'bg-orange-500/20' : 'bg-green-500/20'
            }`}>
              {grantedRole === 'artist' ? (
                <Music className="w-10 h-10 text-orange-500" />
              ) : (
                <Headphones className="w-10 h-10 text-green-500" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {grantedRole === 'artist' ? 'Welcome, Artist!' : "You're in!"}
            </h1>
            <p className="text-zinc-400 text-sm mb-4">
              {grantedRole === 'artist' 
                ? 'You can upload videos and earn from tips!' 
                : 'Enjoy discovering amazing AI music videos!'
              }
            </p>
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              grantedRole === 'artist' 
                ? 'bg-orange-500/20 text-orange-400' 
                : 'bg-blue-500/20 text-blue-400'
            }`}>
              {grantedRole === 'artist' ? (
                <><Music className="w-4 h-4" /> Artist Account</>
              ) : (
                <><Headphones className="w-4 h-4" /> Listener Account</>
              )}
            </span>
            <p className="text-zinc-500 text-xs mt-6">Redirecting to home...</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                <Lock className="w-7 h-7 text-zinc-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Enter Access Code</h1>
              <p className="text-zinc-500 text-sm">AUDIVUE is invite-only. Enter your code to unlock.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="text"
                placeholder="ACCESS CODE"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="bg-zinc-900 border-zinc-800 text-white text-center text-lg tracking-widest h-12 uppercase placeholder:text-zinc-600"
                maxLength={20}
                autoFocus
                data-testid="access-code-input"
              />

              <Button
                type="submit"
                disabled={loading || !code.trim()}
                className="w-full bg-white hover:bg-zinc-200 text-black h-12 text-base font-semibold rounded-full"
                data-testid="unlock-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  'Unlock'
                )}
              </Button>
            </form>

            <p className="text-center text-zinc-600 text-xs mt-6">
              Don't have a code?{' '}
              <a 
                href="mailto:cpowersv@gmail.com?subject=AUDIVUE Access Request" 
                className="text-zinc-400 hover:text-white underline transition-colors"
              >
                Contact the creator
              </a>
              {' '}for access.
            </p>
          </>
        )}
      </div>
      {/* Tiny App Icon */}
      <div className="fixed bottom-6 right-6 z-50">
        <img 
          src={APP_ICON_URL} 
          alt="AUDIVUE" 
          className="w-10 h-10 rounded-xl shadow-xl shadow-purple-500/30 border border-white/10"
        />
      </div>
    </div>
  );
};

export default AccessGatePage;
